import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Wrench, 
  ArrowLeft,
  Building,
  Globe,
  FileText,
  Clock,
  MapPin,
  Phone,
  CheckCircle,
  AlertCircle,
  Shield,
  Award,
  Users,
  TrendingUp,
  BadgeCheck,
  Lock,
  Star,
  DollarSign
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MechanicApplicationPage = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  const [formData, setFormData] = useState({
    business_name: '',
    business_website: '',
    business_license: '',
    services_provided: '',
    years_experience: '',
    address: '',
    phone: '',
    is_bilingual: false,
    languages: '',
    service_areas: '',
    agrees_to_terms: false
  });

  useEffect(() => {
    if (user) {
      checkApplicationStatus();
    } else {
      setCheckingStatus(false);
    }
  }, [user]);

  const checkApplicationStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mechanics/application/status`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setApplicationStatus(response.data);
    } catch (error) {
      console.error('Error checking application status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.agrees_to_terms) {
      toast.error('You must agree to our standards of service');
      return;
    }

    setLoading(true);

    try {
      const applicationData = {
        business_name: formData.business_name,
        business_website: formData.business_website || null,
        business_license: formData.business_license,
        services_provided: formData.services_provided.split(',').map(s => s.trim()).filter(Boolean),
        years_experience: parseInt(formData.years_experience),
        address: formData.address,
        phone: formData.phone,
        is_bilingual: formData.is_bilingual,
        languages: formData.languages ? formData.languages.split(',').map(s => s.trim()).filter(Boolean) : [],
        service_areas: formData.service_areas ? formData.service_areas.split(',').map(s => s.trim().toUpperCase()).filter(Boolean) : [],
        agrees_to_terms: formData.agrees_to_terms
      };

      await axios.post(`${API_URL}/api/mechanics/apply`, applicationData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      toast.success('Application submitted for review');
      checkApplicationStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  // Show application status if already applied
  if (applicationStatus && applicationStatus.status !== 'not_applied') {
    return (
      <div className="min-h-screen bg-slate-900">
        <header className="bg-slate-900 border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                <img src="/logo.png" alt="Mechanicease" className="h-14 w-auto" />
              </Link>
            </div>
          </div>
        </header>

        <main className="max-w-lg mx-auto px-4 py-16">
          <Card className="bg-slate-800/50 border-slate-700" data-testid="application-status-card">
            <CardContent className="p-8 text-center">
              {applicationStatus.status === 'pending' && (
                <>
                  <div className="w-20 h-20 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Clock className="w-10 h-10 text-yellow-500" />
                  </div>
                  <h2 className="font-['Barlow'] text-2xl font-bold text-white mb-3">
                    Application Under Review
                  </h2>
                  <p className="text-slate-400 mb-6">
                    Our team is reviewing your application. We'll email you once a decision is made.
                  </p>
                  <div className="bg-slate-700/50 rounded-lg p-4 text-left">
                    <p className="text-sm text-slate-400 mb-2">Application ID</p>
                    <p className="font-mono text-white">{applicationStatus.application_id}</p>
                  </div>
                </>
              )}
              {applicationStatus.status === 'approved' && (
                <>
                  <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Award className="w-10 h-10 text-green-500" />
                  </div>
                  <h2 className="font-['Barlow'] text-2xl font-bold text-white mb-3">
                    Welcome to the Network
                  </h2>
                  <p className="text-slate-400 mb-6">
                    Your application has been approved. You are now a verified Mechanicease partner.
                  </p>
                  <Button 
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    onClick={() => navigate('/mechanic')}
                    data-testid="go-to-dashboard-btn"
                  >
                    Go to Your Dashboard
                  </Button>
                </>
              )}
              {applicationStatus.status === 'rejected' && (
                <>
                  <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <AlertCircle className="w-10 h-10 text-red-500" />
                  </div>
                  <h2 className="font-['Barlow'] text-2xl font-bold text-white mb-3">
                    Application Not Approved
                  </h2>
                  <p className="text-slate-400 mb-6">
                    We're unable to approve your application at this time. Please contact us if you have questions.
                  </p>
                  <p className="text-sm text-slate-500">
                    Questions? Contact <a href="mailto:support@mechanicease.com" className="text-orange-500">support@mechanicease.com</a>
                  </p>
                </>
              )}
              <div className="mt-6">
                <Link to="/" className="text-slate-500 hover:text-white text-sm">
                  ← Back to Home
                </Link>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Show landing page first (for everyone, logged in or not)
  if (!showForm) {
    return (
      <div className="min-h-screen bg-slate-900">
        <header className="bg-slate-900 border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                <img src="/logo.png" alt="Mechanicease" className="h-14 w-auto" />
              </Link>
            </div>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/" className="inline-flex items-center text-slate-500 hover:text-white mb-8" data-testid="back-link">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-2 mb-6">
              <Shield className="w-4 h-4 text-orange-500" />
              <span className="text-orange-500 text-sm font-medium">PARTNER PROGRAM</span>
            </div>
            <h1 className="font-['Barlow'] text-4xl md:text-5xl font-bold text-white mb-4">
              Join the Mechanicease<br />Partner Network
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-8">
              Connect with customers who value trust and transparency. Build your reputation with verified reviews and grow your business.
            </p>
            
            {user ? (
              <Button 
                className="h-12 px-8 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                onClick={() => setShowForm(true)}
                data-testid="start-application-btn"
              >
                Start Your Application
              </Button>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  className="h-12 px-8 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                  onClick={() => navigate('/register?role=mechanic')}
                  data-testid="signup-btn"
                >
                  Create Partner Account
                </Button>
                <Button 
                  variant="outline"
                  className="h-12 px-8 border-slate-600 text-white hover:bg-slate-800"
                  onClick={() => navigate('/login?redirect=/apply')}
                  data-testid="signin-btn"
                >
                  Sign In to Apply
                </Button>
              </div>
            )}
          </div>

          {/* Why Join Section */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-orange-500" />
                </div>
                <h3 className="font-['Barlow'] text-lg font-bold text-white mb-2">Reach More Customers</h3>
                <p className="text-slate-400 text-sm">
                  Get discovered by customers actively searching for trusted mechanics in their area.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Star className="w-7 h-7 text-orange-500" />
                </div>
                <h3 className="font-['Barlow'] text-lg font-bold text-white mb-2">Build Your Reputation</h3>
                <p className="text-slate-400 text-sm">
                  Collect verified reviews from real customers and showcase your track record.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="w-7 h-7 text-orange-500" />
                </div>
                <h3 className="font-['Barlow'] text-lg font-bold text-white mb-2">Secure Payments</h3>
                <p className="text-slate-400 text-sm">
                  Get paid on time with our secure payment system. No chasing invoices.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* What We Look For */}
          <div className="bg-slate-800/30 rounded-2xl p-8 md:p-10 mb-16">
            <h2 className="font-['Barlow'] text-2xl font-bold text-white mb-6 text-center">What We Look For</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Valid Business License</h4>
                  <p className="text-slate-400 text-sm">Active and verifiable business registration</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Professional Experience</h4>
                  <p className="text-slate-400 text-sm">Demonstrated expertise in auto repair services</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Commitment to Honesty</h4>
                  <p className="text-slate-400 text-sm">Agree to our Standards of Service</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-orange-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <Clock className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">No Unnecessary Delays</h4>
                  <p className="text-slate-400 text-sm">Complete work on time with clear communication</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <h3 className="font-['Barlow'] text-xl font-bold text-white mb-4">Ready to grow your business?</h3>
            {user ? (
              <Button 
                className="h-12 px-8 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                onClick={() => setShowForm(true)}
                data-testid="start-application-btn-2"
              >
                Start Your Application
              </Button>
            ) : (
              <Button 
                className="h-12 px-8 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                onClick={() => navigate('/register?role=mechanic')}
                data-testid="signup-btn-2"
              >
                Create Partner Account
              </Button>
            )}
          </div>
        </main>
      </div>
    );
  }

  // Show application form
  return (
    <div className="min-h-screen bg-slate-900">
      <header className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo.png" alt="Mechanicease" className="h-14 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center text-slate-500 hover:text-white mb-8" data-testid="back-link">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        {/* Application Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-2 mb-4">
            <Shield className="w-4 h-4 text-orange-500" />
            <span className="text-orange-500 text-sm font-medium">PARTNER APPLICATION</span>
          </div>
          <h1 className="font-['Barlow'] text-3xl font-bold text-white mb-3">
            Apply to Join Our Network
          </h1>
          <p className="text-slate-400 max-w-md mx-auto">
            Complete the application below. Our team will review your credentials and contact you.
          </p>
        </div>

        <Card className="bg-slate-800/50 border-slate-700" data-testid="application-form-card">
          <CardContent className="p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Business Name */}
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <Building className="w-4 h-4 text-slate-500" />
                  Business Name *
                </Label>
                <Input
                  name="business_name"
                  placeholder="Your Auto Shop Name"
                  value={formData.business_name}
                  onChange={handleChange}
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 h-11"
                  required
                  data-testid="business-name-input"
                />
              </div>

              {/* Business Website */}
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-slate-500" />
                  Business Website <span className="text-slate-500 text-xs">(Optional)</span>
                </Label>
                <Input
                  name="business_website"
                  type="url"
                  placeholder="https://www.yourshop.com"
                  value={formData.business_website}
                  onChange={handleChange}
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 h-11"
                  data-testid="business-website-input"
                />
              </div>

              {/* Business License */}
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-500" />
                  Business License Number *
                </Label>
                <Input
                  name="business_license"
                  placeholder="License number for verification"
                  value={formData.business_license}
                  onChange={handleChange}
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 h-11"
                  required
                  data-testid="business-license-input"
                />
                <p className="text-xs text-slate-500">We verify all licenses</p>
              </div>

              {/* Services */}
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-slate-500" />
                  Services Provided *
                </Label>
                <Textarea
                  name="services_provided"
                  placeholder="Oil Change, Brake Service, Engine Repair, Transmission Service..."
                  value={formData.services_provided}
                  onChange={handleChange}
                  rows={3}
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                  required
                  data-testid="services-input"
                />
                <p className="text-xs text-slate-500">Separate services with commas</p>
              </div>

              {/* Years of Experience */}
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-500" />
                  Years of Professional Experience *
                </Label>
                <Input
                  name="years_experience"
                  type="number"
                  min="1"
                  placeholder="5"
                  value={formData.years_experience}
                  onChange={handleChange}
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 h-11"
                  required
                  data-testid="experience-input"
                />
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-500" />
                  Business Address *
                </Label>
                <Input
                  name="address"
                  placeholder="123 Main St, City, State ZIP"
                  value={formData.address}
                  onChange={handleChange}
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 h-11"
                  required
                  data-testid="address-input"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-500" />
                  Business Phone *
                </Label>
                <Input
                  name="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 h-11"
                  required
                  data-testid="phone-input"
                />
              </div>

              {/* Bilingual Option */}
              <div className="bg-slate-700/30 rounded-lg p-5 border border-slate-600">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="is_bilingual"
                    checked={formData.is_bilingual}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_bilingual: checked })}
                    className="mt-1 border-slate-500 data-[state=checked]:bg-orange-500"
                    data-testid="bilingual-checkbox"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="is_bilingual" className="text-white font-medium cursor-pointer">
                      I am bilingual / multilingual
                    </Label>
                    <p className="text-sm text-slate-400">
                      Check this if you can serve customers in multiple languages
                    </p>
                  </div>
                </div>
                {formData.is_bilingual && (
                  <div className="mt-4 ml-7">
                    <Label className="text-slate-300 text-sm">Languages spoken (comma-separated)</Label>
                    <Input
                      name="languages"
                      placeholder="English, Mandarin, Punjabi, French, Spanish, Hindi..."
                      value={formData.languages}
                      onChange={handleChange}
                      className="mt-2 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 h-10"
                      data-testid="languages-input"
                    />
                  </div>
                )}
              </div>

              {/* Service Areas */}
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-500" />
                  Service Areas (Postal Codes) *
                </Label>
                <Textarea
                  name="service_areas"
                  placeholder="M5V, M4Y, L5B, L5N..."
                  value={formData.service_areas}
                  onChange={handleChange}
                  rows={2}
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                  required
                  data-testid="service-areas-input"
                />
                <p className="text-xs text-slate-500">
                  Enter the postal code prefixes of areas you can serve, separated by commas. 
                  Customers will use these to find mechanics near them.
                </p>
              </div>

              {/* Terms Agreement */}
              <div className="bg-slate-700/30 rounded-lg p-5 border border-slate-600">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="agrees_to_terms"
                    checked={formData.agrees_to_terms}
                    onCheckedChange={(checked) => setFormData({ ...formData, agrees_to_terms: checked })}
                    className="mt-1 border-slate-500 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                    data-testid="terms-checkbox"
                  />
                  <div className="space-y-2">
                    <label htmlFor="agrees_to_terms" className="text-sm font-medium text-white cursor-pointer leading-tight block">
                      I commit to the Mechanicease Standards of Service
                    </label>
                    <div className="text-xs text-slate-400 space-y-1">
                      <p>By checking this box, I agree to:</p>
                      <ul className="list-disc list-inside space-y-0.5 text-slate-500">
                        <li>Provide honest assessments and recommendations</li>
                        <li>Use transparent, fair pricing with no hidden fees</li>
                        <li>Only perform necessary repairs and services</li>
                        <li className="text-orange-400 font-medium">No unnecessary delays - complete work on time with clear communication</li>
                        <li>Maintain professional communication with customers</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                disabled={loading}
                data-testid="submit-application-btn"
              >
                {loading ? 'Submitting Application...' : 'Submit Application'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default MechanicApplicationPage;
